<template>
  <div class="service-orders-map">
    <div class="d-flex flex-wrap align-items-center px-1 pt-1">
      <b-form-checkbox switch :checked="heatEnabled" class="mr-2 mb-1" @change="$emit('update:heatEnabled', $event)">
        {{ $t('orderMap.heat') }}
      </b-form-checkbox>
      <b-form-checkbox v-if="heatEnabled" switch :checked="showUnits" class="mb-1" @change="$emit('update:showUnits', $event)">
        {{ $t('orderMap.showUnits') }}
      </b-form-checkbox>
    </div>
    <p v-if="heatLoading" class="px-1 mb-1" role="status">{{ $t('orderMap.heatLoading') }}</p>
    <p v-if="heatError" class="px-1 mb-1 text-danger" role="alert">{{ $t('orderMap.heatError') }}</p>
    <div v-if="heatEnabled" class="px-1 mb-1">
      <small>{{ $t('orderMap.density') }}</small>
      <div class="d-flex align-items-center">
        <small>{{ $t('orderMap.low') }}</small>
        <span class="heat-legend mx-1" aria-hidden="true"></span>
        <small>{{ $t('orderMap.high') }}</small>
      </div>
    </div>
    <div class="d-flex flex-wrap p-1 border-bottom">
      <span v-for="status in statuses" :key="status.id" class="mr-2">
        <span class="status-dot" :style="{ backgroundColor: status.color }"></span>
        {{ $t(`service_orders.${status.status}`) }}
      </span>
    </div>
    <p v-if="missingCoordinates" class="px-1 mb-1 text-muted">
      {{ $t('orderMap.missing', { count: missingCoordinates }) }}
    </p>
    <p v-if="!units.length" class="p-2">{{ $t('orderMap.empty') }}</p>
    <p v-else-if="failed" class="p-2 text-danger" role="alert">{{ $t('orderMap.error') }}</p>
    <div v-show="units.length && !failed" ref="mapCanvas" :id="mapId" class="map-canvas"></div>
    <small class="px-1 py-1 text-muted">{{ $t('orderMap.colorRule') }}</small>
    <b-modal ref="documentModal" :id="`${mapId}-document`" :title="popupText.pdf" size="xl" hide-footer @hidden="closeDocument">
      <p v-if="documentLoading" role="status">{{ popupText.loading }}</p>
      <p v-else-if="documentError" role="alert" class="text-danger">{{ popupText.documentError }}</p>
      <iframe v-else-if="documentUrl" :src="documentUrl" :title="popupText.pdf" class="document-preview"></iframe>
    </b-modal>
  </div>
</template>

<script>
import { Map as OMS } from '@omnicloud/maps-sdk';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';
import useAppConfig from '@core/app-config/useAppConfig';
import { getCspNonce } from '@/utils/cspNonce';
import { BFormCheckbox, BModal } from 'bootstrap-vue';
import axios from '@/libs/axios';
import router from '@/router';

const POPUP_TEXT = {
  es: { orders: 'Órdenes de servicio de estos filtros', pdf: 'Ver PDF', edit: 'Editar formulario', loading: 'Cargando documento…', checking: 'Comprobando PDF…', missing: 'Sin PDF generado', checkError: 'No se pudo comprobar el PDF', noForm: 'Sin formulario creado', unknown: 'Sin estado', documentError: 'No se pudo abrir el PDF. Puede que ya no esté disponible.' },
  en: { orders: 'Service orders matching these filters', pdf: 'View PDF', edit: 'Edit form', loading: 'Loading document…', checking: 'Checking PDF…', missing: 'No generated PDF', checkError: 'Could not check the PDF', noForm: 'No form created', unknown: 'No status', documentError: 'Could not open the PDF. It may no longer be available.' },
  pt: { orders: 'Ordens de serviço destes filtros', pdf: 'Ver PDF', edit: 'Editar formulário', loading: 'Carregando documento…', checking: 'Verificando PDF…', missing: 'Sem PDF gerado', checkError: 'Não foi possível verificar o PDF', noForm: 'Sem formulário criado', unknown: 'Sem estado', documentError: 'Não foi possível abrir o PDF. Ele pode não estar mais disponível.' },
};

const ICONS = {
  orange: 'apartment-orange.png', '#ffa500': 'apartment-orange.png',
  blue: 'apartment-3-blue.png', '#0000ff': 'apartment-3-blue.png',
  yellow: 'apartment-yellow.png', '#ffff00': 'apartment-yellow.png',
  green: 'apartment-3-green.png', '#008000': 'apartment-3-green.png',
  gray: 'apartment-grey.png', grey: 'apartment-grey.png', '#808080': 'apartment-grey.png',
};

const dateTimestamp = value => {
  if (!value) return null;
  const date = moment(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
  return date.isValid() ? date.valueOf() : null;
};

const orderTimestamp = order => dateTimestamp(order.attention_date)
  ?? dateTimestamp(order.created_at) ?? -Infinity;

export default {
  components: { BFormCheckbox, BModal },
  props: {
    orders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    statuses: { type: Array, default: () => [] },
    heatEnabled: { type: Boolean, default: false },
    showUnits: { type: Boolean, default: true },
  },
  i18n: {
    messages: {
      es: { orderMap: { heat: 'Mapa de calor', showUnits: 'Mostrar instalaciones', heatLoading: 'Cargando capa de calor…', heatError: 'No se pudo mostrar la capa de calor. Puedes seguir usando los marcadores e intentar activarla de nuevo.', density: 'Concentración relativa de órdenes cargadas en la zona visible', low: 'Menor', high: 'Mayor', missing: '{count} unidades sin coordenadas válidas.', empty: 'No hay unidades con ubicación para estos filtros.', error: 'No se pudo cargar el mapa. Vuelve a actualizar.', colorRule: 'Color del icono según la fecha de atención más reciente por unidad; si falta o no es válida, se usa la fecha de creación.' } },
      en: { orderMap: { heat: 'Heatmap', showUnits: 'Show facilities', heatLoading: 'Loading heat layer…', heatError: 'Could not display the heat layer. You can keep using markers and try enabling it again.', density: 'Relative concentration of loaded orders in the visible area', low: 'Lower', high: 'Higher', missing: '{count} units without valid coordinates.', empty: 'No units with a location match these filters.', error: 'Could not load the map. Please refresh again.', colorRule: 'Icon color by the latest attention date per unit; missing or invalid dates fall back to the creation date.' } },
      pt: { orderMap: { heat: 'Mapa de calor', showUnits: 'Mostrar instalações', heatLoading: 'Carregando camada de calor…', heatError: 'Não foi possível mostrar a camada de calor. Você pode continuar usando os marcadores e tentar ativá-la novamente.', density: 'Concentração relativa de ordens carregadas na área visível', low: 'Menor', high: 'Maior', missing: '{count} unidades sem coordenadas válidas.', empty: 'Nenhuma unidade com localização para estes filtros.', error: 'Não foi possível carregar o mapa. Atualize novamente.', colorRule: 'Cor do ícone pela data de atendimento mais recente por unidade; se ausente ou inválida, usa a data de criação.' } },
    },
  },
  data() {
    return {
      mapId: `service-orders-${uuidv4()}`,
      failed: false,
      heatLoading: false,
      heatError: false,
      selectedUnitId: null,
      documentLoading: false,
      documentError: false,
      documentUrl: null,
    };
  },
  computed: {
    heatmapData() {
      const counts = new Map();
      const seen = new Set();
      this.orders.forEach(order => {
        if (seen.has(String(order.id))) return;
        seen.add(String(order.id));
        const key = String(order.id_unit);
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      return this.units.map(order => ({
        position: [order.unit.coordinates.lng, order.unit.coordinates.lat],
        weight: counts.get(String(order.id_unit)) || 0,
      }));
    },
    popupText() {
      return POPUP_TEXT[this.$i18n.locale.split('-')[0]] || POPUP_TEXT.en;
    },
    popupOrders() {
      return this.orders.filter(order => String(order.id_unit) === String(this.selectedUnitId))
        .sort((a, b) => orderTimestamp(b) - orderTimestamp(a) || Number(b.id) - Number(a.id));
    },
    selectedOrders() {
      const selected = new Map();
      this.orders.forEach(order => {
        if (!order.unit) return;
        const key = String(order.id_unit);
        const previous = selected.get(key);
        if (!previous || orderTimestamp(order) > orderTimestamp(previous)
          || (orderTimestamp(order) === orderTimestamp(previous) && Number(order.id) > Number(previous.id))) {
          selected.set(key, order);
        }
      });
      return Array.from(selected.values());
    },
    units() {
      return this.selectedOrders.reduce((units, order) => {
        const coordinates = [
          order.unit.coordinates,
          { lat: order.unit.router?.lat, lng: order.unit.router?.lng ?? order.unit.router?.lon },
        ].find(value => {
          if (!value || !['string', 'number'].includes(typeof value.lat)
            || !['string', 'number'].includes(typeof value.lng)
            || String(value.lat).trim() === '' || String(value.lng).trim() === '') return false;
          const lat = Number(value.lat);
          const lng = Number(value.lng);
          return Number.isFinite(lat) && Math.abs(lat) <= 90
            && Number.isFinite(lng) && Math.abs(lng) <= 180;
        });
        if (coordinates) {
          units.push({
            ...order,
            unit: {
              ...order.unit,
              coordinates: { lat: Number(coordinates.lat), lng: Number(coordinates.lng) },
            },
          });
        }
        return units;
      }, []);
    },
    missingCoordinates() {
      return this.selectedOrders.length - this.units.length;
    },
  },
  watch: {
    loading(value) {
      if (!value) this.$nextTick(() => this.fitInitialBounds());
    },
    heatEnabled() {
      this.updateHeatmap();
      this.updateMarkerVisibility();
    },
    showUnits() {
      this.updateMarkerVisibility();
    },
    heatmapData() {
      this.updateHeatmap();
    },
    orders() {
      this.$nextTick(() => {
        this.updatePopup();
        this.queueDocumentChecks();
      });
    },
    units() {
      if (this.mapReady) this.renderMarkers();
      else if (!this.mapSdk) this.$nextTick(() => this.initMap());
    },
    '$i18n.locale'(locale) {
      if (this.mapSdk) this.mapSdk.setLocale(locale);
      this.updatePopup();
    },
  },
  mounted() {
    this.documentStates = new Map();
    this.documentWorkers = 0;
    this.$refs.mapCanvas.addEventListener('marker:click', this.onMarkerClick);
    this.initMap();
  },
  beforeDestroy() {
    this.disposed = true;
    this.$refs.mapCanvas.removeEventListener('marker:click', this.onMarkerClick);
    if (this.documentCheckController) this.documentCheckController.abort();
    if (this.infoWindowCloseListener) this.infoWindowCloseListener.remove();
    if (this.viewportListeners) this.viewportListeners.forEach(listener => listener.remove());
    this.closeDocument();
    if (this.heatOverlay) this.heatOverlay.finalize();
    if (this.mapSdk) this.mapSdk.destroy();
  },
  methods: {
    updateMarkerVisibility() {
      if (!this.mapReady || this.disposed) return;
      const map = this.mapSdk.map;
      if (this.heatEnabled && !this.showUnits && !this.heatLoading && !this.heatError) {
        map.hideMarkers();
        map.infowindow?.close();
        this.selectedUnitId = null;
        if (this.documentCheckController) this.documentCheckController.abort();
      } else {
        map.showMarkers();
      }
    },
    failHeatmap() {
      if (this.disposed) return;
      this.heatError = true;
      this.heatLoading = false;
      this.$emit('update:heatEnabled', false);
      this.updateMarkerVisibility();
      this.$nextTick(() => {
        if (this.heatOverlay) this.heatOverlay.finalize();
        this.heatOverlay = null;
      });
    },
    async updateHeatmap() {
      const revision = this.heatRevision = (this.heatRevision || 0) + 1;
      if (this.disposed || !this.mapReady) return;
      if (!this.heatEnabled) {
        this.heatLoading = false;
        if (this.heatOverlay) this.heatOverlay.setMap(null);
        return;
      }
      this.heatError = false;
      this.heatLoading = true;
      try {
        const modules = await Promise.allSettled([
          import('@deck.gl/google-maps'),
          import('@deck.gl/aggregation-layers'),
        ]);
        if (this.disposed || revision !== this.heatRevision) return;
        const failure = modules.find(result => result.status === 'rejected');
        if (failure) throw failure.reason;
        const { GoogleMapsOverlay } = modules[0].value;
        const { HeatmapLayer } = modules[1].value;
        if (!this.heatOverlay) {
          this.heatOverlay = new GoogleMapsOverlay({
            interleaved: false,
            style: { pointerEvents: 'none' },
            onError: () => this.failHeatmap(),
          });
        }
        this.heatOverlay.setProps({
          layers: [new HeatmapLayer({
            id: `${this.mapId}-heat`,
            data: this.heatmapData,
            getPosition: point => point.position,
            getWeight: point => point.weight,
            aggregation: 'SUM',
            radiusPixels: 40,
            opacity: 0.65,
            weightsTextureSize: 512,
            colorRange: [[255, 255, 178], [254, 217, 118], [254, 178, 76], [253, 141, 60], [240, 59, 32], [189, 0, 38]],
          })],
        });
        this.heatOverlay.setMap(this.mapSdk.map.map);
        this.heatLoading = false;
        this.updateMarkerVisibility();
      } catch (error) {
        if (revision === this.heatRevision) this.failHeatmap();
      }
    },
    onMarkerClick({ detail }) {
      if (this.documentCheckController) this.documentCheckController.abort();
      this.documentCheckController = new AbortController();
      this.documentStates = new Map();
      this.documentWorkers = 0;
      this.selectedUnitId = detail.id;
      const infoWindow = this.mapSdk.map.infowindow;
      if (!this.infoWindowCloseListener) {
        this.infoWindowCloseListener = infoWindow.addListener('closeclick', () => {
          this.selectedUnitId = null;
          this.documentCheckController.abort();
        });
      }
      this.$nextTick(() => {
        this.updatePopup();
        this.queueDocumentChecks();
      });
    },
    updatePopup() {
      if (this.disposed || this.selectedUnitId == null) return;
      const infoWindow = this.mapSdk?.map.infowindow;
      if (!infoWindow) return;
      let content = infoWindow.getContent();
      if (typeof content === 'string') {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = content;
        content = wrapper;
      }
      if (!content?.querySelector) return;
      const previous = content.querySelector('.service-orders-popup');
      if (previous) previous.remove();
      const section = document.createElement('section');
      section.className = 'service-orders-popup';
      section.style.cssText = 'border-top:1px solid #ccc;margin-top:12px;padding-top:12px;max-height:280px;overflow:auto';
      const heading = document.createElement('h4');
      heading.textContent = `${this.popupText.orders} (${this.popupOrders.length})`;
      section.appendChild(heading);
      this.popupOrders.forEach(order => {
        const row = document.createElement('div');
        row.style.cssText = 'padding:8px 0;border-bottom:1px solid #ddd';
        const reference = document.createElement('strong');
        reference.textContent = order.reference_number || String(order.id);
        row.appendChild(reference);
        const status = document.createElement('div');
        const dot = document.createElement('span');
        dot.style.cssText = 'display:inline-block;width:10px;height:10px;border-radius:50%;margin-right:5px';
        dot.style.backgroundColor = order.latest_status?.color || '#808080';
        status.appendChild(dot);
        status.appendChild(document.createTextNode(order.latest_status
          ? this.$t(`service_orders.${order.latest_status.status}`) : this.popupText.unknown));
        row.appendChild(status);
        const attentionDate = dateTimestamp(order.attention_date);
        const details = [
          [this.$t('service_orders.attention_date'), attentionDate == null ? '—' : moment(attentionDate).format('DD/MM/YYYY HH:mm:ss')],
          [this.$t('maintenance.maintenance_crew'), order.maintenance_crew?.description || '—'],
          [this.$t('maintenance.template'), order.template?.name || '—'],
        ];
        details.forEach(([label, value]) => {
          const detail = document.createElement('div');
          detail.textContent = `${label}: ${value}`;
          row.appendChild(detail);
        });
        const formId = order.maintenance_form_answer?.id;
        if (formId && this.$can('downloadDocument', 'MaintenanceForm')) {
          const state = this.documentStates.get(String(formId));
          if (state === 'available') {
            const pdf = document.createElement('button');
            pdf.type = 'button';
            pdf.className = 'btn btn-link p-0';
            pdf.textContent = this.popupText.pdf;
            pdf.addEventListener('click', () => this.openDocument(formId));
            row.appendChild(pdf);
          } else {
            const label = document.createElement('small');
            label.textContent = state === 'missing' ? this.popupText.missing
              : state === 'error' ? this.popupText.checkError : this.popupText.checking;
            row.appendChild(label);
          }
        } else if (!formId) {
          const label = document.createElement('small');
          label.textContent = this.popupText.noForm;
          row.appendChild(label);
        }
        if (formId && this.$can('update', 'MaintenanceForm')) {
          const edit = document.createElement('a');
          edit.textContent = this.popupText.edit;
          edit.href = router.resolve({ name: 'maintenanceFormsEdit', params: { id_form: formId } }).href;
          edit.target = '_blank';
          edit.rel = 'noopener noreferrer';
          edit.style.marginLeft = '12px';
          row.appendChild(edit);
        }
        section.appendChild(row);
      });
      content.appendChild(section);
      infoWindow.setContent(content);
    },
    queueDocumentChecks() {
      if (this.disposed || this.selectedUnitId == null || !this.$can('downloadDocument', 'MaintenanceForm')
        || !this.documentCheckController || this.documentCheckController.signal.aborted) return;
      while (this.documentWorkers < 3) {
        const order = this.popupOrders.find(item => item.maintenance_form_answer?.id
          && !this.documentStates.has(String(item.maintenance_form_answer.id)));
        if (!order) break;
        this.checkDocument(order.maintenance_form_answer.id);
      }
    },
    async checkDocument(formId) {
      const controller = this.documentCheckController;
      this.documentWorkers += 1;
      this.documentStates.set(String(formId), 'loading');
      try {
        const { data } = await axios.get(`/v1/maintenance-forms/download-store-document/${formId}`, {
          params: { check_only: true }, timeout: 30000, signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        this.documentStates.set(String(formId), data?.exists === true ? 'available' : 'error');
      } catch (error) {
        if (controller.signal.aborted) return;
        this.documentStates.set(String(formId), error.response?.status === 404 ? 'missing' : 'error');
      } finally {
        if (!controller.signal.aborted && !this.disposed) {
          this.documentWorkers -= 1;
          this.updatePopup();
          this.queueDocumentChecks();
        }
      }
    },
    async openDocument(formId) {
      if (!this.$can('downloadDocument', 'MaintenanceForm')) return;
      this.closeDocument();
      const controller = new AbortController();
      this.pdfController = controller;
      this.documentLoading = true;
      this.documentError = false;
      this.$refs.documentModal.show();
      try {
        const { data } = await axios.get(`/v1/maintenance-forms/download-store-document/${formId}`, {
          responseType: 'blob', headers: { Accept: 'application/pdf' },
          timeout: 30000, signal: controller.signal,
        });
        if (!(data instanceof Blob) || await data.slice(0, 5).text() !== '%PDF-') throw new Error('Invalid PDF');
        if (controller.signal.aborted || this.disposed) return;
        this.documentUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      } catch (error) {
        if (!controller.signal.aborted && !this.disposed) this.documentError = true;
      } finally {
        if (!controller.signal.aborted && !this.disposed) this.documentLoading = false;
      }
    },
    closeDocument() {
      if (this.pdfController) this.pdfController.abort();
      if (this.documentUrl) URL.revokeObjectURL(this.documentUrl);
      this.documentUrl = null;
      this.documentLoading = false;
    },
    async initMap() {
      if (this.disposed || this.mapSdk || !this.units.length) return;
      const { skin } = useAppConfig();
      const sdk = new OMS({
        token: window.localStorage.accessToken,
        apiUrl: `${window.__env.VUE_APP_BASE_URL}/v1`,
        locale: this.$i18n.locale,
      });
      this.mapSdk = sdk;
      this.renderedOrders = new Map();
      const ready = sdk.onMapReady();
      try {
        await sdk.setConfig({
          container: `#${this.mapId}`,
          latLng: { lat: 19.39068, lng: -99.2836975 },
          gmapsKey: window.__env.VUE_APP_GMAPS_KEY,
          locale: this.$i18n.locale,
          darkMode: skin.value === 'dark',
          mapId: skin.value === 'dark' ? 'eac5c4d4f71b58d6' : 'SBO',
          nonce: getCspNonce(),
        });
        await ready;
        if (this.disposed) {
          sdk.destroy();
          return;
        }
        this.mapReady = true;
        this.viewportListeners = ['dragstart', 'zoom_changed'].map(event =>
          sdk.map.map.addListener(event, () => { this.viewportInitialized = true; }));
        this.updateHeatmap();
        this.updateMarkerVisibility();
        await this.renderMarkers();
      } catch (error) {
        if (!this.disposed) this.failed = true;
      }
    },
    async renderMarkers() {
      if (this.disposed || !this.mapReady) return;
      const sdk = this.mapSdk;
      const changed = this.units.filter(order => this.renderedOrders.get(String(order.id_unit)) !== order);
      if (!changed.length) return;
      try {
        const markers = changed.map(order => {
          const marker = sdk.buildMarker(order.unit);
          const color = String(order.latest_status?.color || '').trim().toLowerCase();
          const filename = ICONS[color] || 'apartment-grey.png';
          const icon = { url: `${process.env.BASE_URL}img/mapiconscollection-markers/${filename}` };
          return { ...marker, icon, properties: { ...marker.properties, icon } };
        });
        await sdk.setMarker(markers);
        if (this.disposed) return;
        changed.forEach(order => this.renderedOrders.set(String(order.id_unit), order));
        this.fitInitialBounds();
      } catch (error) {
        if (!this.disposed) this.failed = true;
      }
    },
    fitInitialBounds() {
      if (this.disposed || !this.mapReady || this.loading || this.viewportInitialized || !this.units.length) return;
      this.viewportInitialized = true;
      const bounds = new google.maps.LatLngBounds();
      this.units.forEach(order => bounds.extend(order.unit.coordinates));
      if (this.units.length === 1) {
        const { lat, lng } = this.units[0].unit.coordinates;
        this.mapSdk.setCenter(lat, lng, 15);
      } else {
        this.mapSdk.map.map.fitBounds(bounds);
      }
    },
  },
};
</script>

<style scoped>
.service-orders-map {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.map-canvas {
  flex: 1;
  min-height: 200px;
}
.document-preview {
  width: 100%;
  height: 65vh;
  border: 0;
}
.heat-legend {
  display: inline-block;
  width: 120px;
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(to right, #ffffb2, #fed976, #feb24c, #fd8d3c, #f03b20, #bd0026);
}
.status-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 4px;
}
</style>
